import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Alert,
} from "@mui/material";
import { 
  Close as CloseIcon, 
  Warning as WarningIcon,
  Delete as DeleteIcon 
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDeleteEtablissement } from "../../../api/etablissement";
import { CustomLoader } from "../../../components/CustomLoader/CustomLoader";


interface ModalDeleteEtablissementProps {
  open: boolean;
  onClose: () => void;
  etablissement: {
    id: number;
    name: string;
    signs?: any[];
  } | null;
  onSuccess?: () => void;
}

export const ModalDeleteEtablissement: React.FC<ModalDeleteEtablissementProps> = ({
  open,
  onClose,
  etablissement,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const { mutate: deleteEtablissement, isPending } = useDeleteEtablissement(() => {
    onClose();
    onSuccess?.();
  });

  const handleDelete = () => {
    if (etablissement) {
      deleteEtablissement(etablissement.id);
    }
  };

  const hasSignsWarning = etablissement?.signs && etablissement.signs.length > 0;

  return (
    <>
      {isPending && <CustomLoader isActive={isPending} />}
      
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "error.main",
            color: "white",
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon />
            <Typography variant="h6" component="span">
              {t("etablissement.confirmDelete", "Confirm Delete")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ color: "white" }}
            disabled={isPending}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body1">
              {t("etablissement.deleteMessage", "Are you sure you want to delete this etablissement?")}
            </Typography>
            
            {etablissement && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "grey.50",
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t("etablissement.etablissementToDelete", "Etablissement to delete:")}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {etablissement.name}
                </Typography>
                {etablissement.signs && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t("etablissement.signsCount", "{{count}} signs", { count: etablissement.signs.length })}
                  </Typography>
                )}
              </Box>
            )}

            {hasSignsWarning && (
              <Alert severity="warning" sx={{ borderRadius: "8px" }}>
                <Typography variant="body2">
                  {t("etablissement.deleteWarning", "This etablissement has {{count}} associated signs. Deleting it will also affect these signs.", 
                    { count: etablissement.signs?.length || 0 })}
                </Typography>
              </Alert>
            )}

            <Alert severity="error" sx={{ borderRadius: "8px" }}>
              <Typography variant="body2">
                {t("etablissement.deleteWarningFinal", "This action cannot be undone.")}
              </Typography>
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={isPending}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              minWidth: "80px",
            }}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={isPending}
            startIcon={<DeleteIcon />}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              minWidth: "100px",
            }}
          >
            {t("common.delete", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};